import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { CustomInput } from "../components/CustomInput";
import PrimaryButton from "../components/PrimaryButton";
import toast from "react-hot-toast";
import { MobileResponsive } from "./MobileResponsive";
import {
  changeCommission,
  fetchCommission,
  updateCommissionData,
} from "../features/commissionSlice";
import { useDispatch } from "react-redux";
import { convertToNumber } from "../utils/convertToNum";
import DynamicMetaTags from "../components/DynamicMetaTags";

const Pricing = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pricing, setPricing] = useState({
    deliveryCostfor1stMile: { value: "", disabled: true },
    deliveryCostforSubsequentMile: { value: "", disabled: true },
    commissionPercentageFromDriver: { value: "", disabled: true },
  });

  // Fetch commission data on component mount
  useEffect(() => {
    const loadCommissionData = async () => {
      try {
        setIsLoading(true);
        const resultAction = await dispatch(fetchCommission());
        if (fetchCommission.fulfilled.match(resultAction)) {
          setData(resultAction.payload);
          setPricing({
            deliveryCostfor1stMile: { value: resultAction.payload?.first_mile || "", disabled: true },
            deliveryCostforSubsequentMile: {
              value: resultAction.payload?.subsequent_half_mile || "",
              disabled: true,
            },
            commissionPercentageFromDriver: {
              value: resultAction.payload?.commission || "",
              disabled: true,
            },
          });
        }
      } catch (error) {
        console.error("Error loading commission data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCommissionData();
  }, [dispatch]);

  const [validationErrors, setValidationErrors] = useState({
    deliveryCostfor1stMile: false,
    deliveryCostforSubsequentMile: false,
    commissionPercentageFromDriver: false,
  });

  // Additional state to track specific error messages
  const [errorMessages, setErrorMessages] = useState({
    deliveryCostfor1stMile: "",
    deliveryCostforSubsequentMile: "",
    commissionPercentageFromDriver: "",
  });

  const handlePriceChange = (e, key) => {
    let inputValue = e.target.value.replace(/[^0-9.]/g, "");
    if (inputValue.split(".").length > 2) return;

    // Special validation for commission percentage (0-100)
    if (key === "commissionPercentageFromDriver") {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue > 100) {
        inputValue = "100";
      }
    }

    setPricing((prevState) => ({
      ...prevState,
      [key]: { ...prevState[key], value: inputValue },
    }));

    if (inputValue) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, [key]: false }));
    }
  };

  const togglePriceEdit = (key) => {
    setPricing((prevState) => {
      const isDisabled = prevState[key].disabled;
      const currentValue = prevState[key].value;

      if (!isDisabled) {
        // Validate commission percentage when saving
        if (key === "commissionPercentageFromDriver") {
          const commissionValue = parseFloat(currentValue);
          if (
            !isNaN(commissionValue) &&
            (commissionValue < 0 || commissionValue > 100)
          ) {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              [key]: true,
            }));
            setErrorMessages((prev) => ({
              ...prev,
              [key]: "Commission must be between 0-100%",
            }));
            // Don't save, keep in edit mode
            return prevState;
          }
        }

        if (!currentValue) {
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [key]: true,
          }));
          setErrorMessages((prev) => ({
            ...prev,
            [key]: "Please enter a valid value",
          }));
        } else {
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [key]: false,
          }));
          setErrorMessages((prev) => ({
            ...prev,
            [key]: "",
          }));
        }
      }

      return {
        ...prevState,
        [key]: {
          value: isDisabled
            ? currentValue
            : String(currentValue).replace(/^£/, ""),
          disabled: !isDisabled,
        },
      };
    });
  };

  // Check if any field is still in edit mode
  const isAnyFieldInEditMode = () => {
    return Object.values(pricing).some((field) => !field.disabled);
  };

  // Check if commission value is within valid range
  const validateCommissionRange = () => {
    const commissionValue = parseFloat(
      pricing.commissionPercentageFromDriver.value
    );
    return (
      isNaN(commissionValue) || (commissionValue >= 0 && commissionValue <= 100)
    );
  };

  // Check if any validation error exists
  const hasValidationErrors = () => {
    return (
      Object.values(validationErrors).some((error) => error) ||
      !validateCommissionRange()
    );
  };

  // Check if any field is empty
  const hasEmptyFields = () => {
    return Object.entries(pricing).some(([key, field]) => field.value === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for validation errors
    let errors = {
      deliveryCostfor1stMile: pricing.deliveryCostfor1stMile.value === "",
      deliveryCostforSubsequentMile:
        pricing.deliveryCostforSubsequentMile.value === "",
      commissionPercentageFromDriver:
        pricing.commissionPercentageFromDriver.value === "",
    };

    // Check commission percentage range
    const commissionValue = parseFloat(
      pricing.commissionPercentageFromDriver.value
    );
    if (
      !isNaN(commissionValue) &&
      (commissionValue < 0 || commissionValue > 100)
    ) {
      errors.commissionPercentageFromDriver = true;
      setErrorMessages((prev) => ({
        ...prev,
        commissionPercentageFromDriver: "Commission must be between 0-100%",
      }));
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        commissionPercentageFromDriver: "",
      }));
    }

    setValidationErrors(errors);

    // Check if all inputs are saved (disabled)
    let isAllInputsSaved = Object.values(pricing).every(
      (item) => item.disabled
    );

    // If any input is still in edit mode or there are validation errors, do not submit
    if (!isAllInputsSaved || Object.values(errors).some((error) => error)) {
      toast.error("Please save all inputs and ensure values are valid.");
      return;
    }

    setIsSubmitting(true);

    try {
      const resultAction = await dispatch(
        changeCommission({
          commission: convertToNumber(
            pricing.commissionPercentageFromDriver.value
          ),
          firstMile: convertToNumber(pricing.deliveryCostfor1stMile.value),
          subsequentMiles: convertToNumber(
            pricing.deliveryCostforSubsequentMile.value
          ),
        })
      );

      if (changeCommission.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);
      }
    } catch (error) {
      toast.error("An error occurred while updating commission and mile pricing");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Button should be disabled if:
  // 1. Any field is in edit mode (not saved)
  // 2. There are validation errors
  // 3. Any field is empty
  // 4. Form is currently submitting
  const isButtonDisabled =
    isAnyFieldInEditMode() ||
    hasValidationErrors() ||
    hasEmptyFields() ||
    isSubmitting;

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Transparent Pricing & Commission Structure"
        pageDescription="Explore Beep's transparent pricing structure! Discover the costs for the first mile, subsequent miles, and the commission percentage for drivers, ensuring clarity in your delivery expenses."
        pageKeywords="Beep, pricing structure, delivery costs, first mile pricing, subsequent mile pricing, driver commission"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between">
            <div className="flex items-center gap-3">
              {/* <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div> */}
              <span className="font-semibold text-[20px]">Pricing</span>
            </div>
          </div>
          <div className="w-full flex items-start py-4">
            <div className="w-[41%]">
              {isLoading ? (
                ""
              ) : (
                <form className="w-[90%]" onSubmit={handleSubmit}>
                  <div className="py-5 flex flex-col gap-5">
                    <CustomInput
                      label="Delivery cost for the 1st mile"
                      value={pricing.deliveryCostfor1stMile.value}
                      onChange={(e) =>
                        handlePriceChange(e, "deliveryCostfor1stMile")
                      }
                      isDisabled={pricing.deliveryCostfor1stMile.disabled}
                      toggleEdit={() =>
                        togglePriceEdit("deliveryCostfor1stMile")
                      }
                      validationError={validationErrors.deliveryCostfor1stMile}
                      errorMessage={errorMessages.deliveryCostfor1stMile}
                    />
                    <CustomInput
                      label="Delivery cost for the subsequent mile"
                      value={pricing.deliveryCostforSubsequentMile.value}
                      onChange={(e) =>
                        handlePriceChange(e, "deliveryCostforSubsequentMile")
                      }
                      isDisabled={
                        pricing.deliveryCostforSubsequentMile.disabled
                      }
                      toggleEdit={() =>
                        togglePriceEdit("deliveryCostforSubsequentMile")
                      }
                      validationError={
                        validationErrors.deliveryCostforSubsequentMile
                      }
                      errorMessage={errorMessages.deliveryCostforSubsequentMile}
                    />
                    <CustomInput
                      label="Percentage of commission from driver"
                      value={pricing.commissionPercentageFromDriver.value}
                      onChange={(e) =>
                        handlePriceChange(e, "commissionPercentageFromDriver")
                      }
                      isDisabled={
                        pricing.commissionPercentageFromDriver.disabled
                      }
                      toggleEdit={() =>
                        togglePriceEdit("commissionPercentageFromDriver")
                      }
                      validationError={
                        validationErrors.commissionPercentageFromDriver
                      }
                      errorMessage={
                        errorMessages.commissionPercentageFromDriver
                      }
                      type="percentage"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-5 py-3 w-[35%]">
                    <PrimaryButton
                      text={isSubmitting ? "Updating..." : "Confirm"}
                      width="w-full"
                      uppercase={false}
                      mutation={true}
                      type="submit"
                      disabled={isButtonDisabled}
                    />
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
